import type { ChangeEvent, ClipboardEvent, FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useCreateIssueDraft } from '#src/app/pages/homepage/shared/hooks';
import { isLoaded, isLoading } from '#src/app/shared/utils/async';

import { useInputSourcesArm } from '../../../../hooks/useInputSourcesArm';
import type { FormHelperVariant, InputHeadingVariant } from '../../../../hooks/useInputSourcesArm';

import type { AttachedPost, Chip } from './attachedPost';
import { toChip } from './attachedPost';
import { getPreviewSuccess, mapPreviewErrorToCode, mapReasonToErrorCode } from './errorCodes';
import type { SocialPostErrorCode } from './errorCodes';
import { handlePastedInput } from './pastedInput';
import { submitFreshInput } from './submitFreshInput';
import { useGenerateSocialPostDraft } from './useGenerateSocialPostDraft';
import { useSocialPostPreview } from './useSocialPostPreview';
import type { ClassifyOptions } from './utils';

export type { SocialPostErrorCode } from './errorCodes';

type SocialPostUrlFormHookResult = ModelHookResult<
	{
		isSubmitting: boolean;
		// True while a supported link has been recognized and its preview is still in flight —
		// the UI shows a loading placeholder during this window rather than the chip.
		isResolvingPreview: boolean;
		error: SocialPostErrorCode | undefined;
		chip: Chip | undefined;
		/** Whether the hero accepts any allowlisted link rather than TikTok only; picks the multi-source copy. */
		multiSource: boolean;
		/** Whether errors show inside the input, next to the submit button, rather than in the callout below it. */
		inlineMessages: boolean;
		helper: FormHelperVariant | undefined;
		heading: InputHeadingVariant;
	},
	{
		onSubmit: (event: FormEvent<HTMLFormElement>) => void;
		onPaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
		onInputChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
		onRemoveChip: () => void;
	}
>;

function useArm() {
	const {
		data: { multiSource, inlineMessages, helper, heading },
	} = useInputSourcesArm();
	const classifyOptions = useMemo<ClassifyOptions>(() => ({ multiInputSources: multiSource }), [multiSource]);
	return { classifyOptions, arm: { multiSource, inlineMessages, helper, heading } };
}

type MutationState = Parameters<typeof isLoading>[0] & Parameters<typeof isLoaded>[0];

/** Whether either submit path is in flight, or already succeeded and is redirecting. */
function useIsSubmitting(generateDraft: MutationState, createIssueDraft: MutationState): boolean {
	return useMemo(
		() => [generateDraft, createIssueDraft].some((mutation) => isLoading(mutation) || isLoaded(mutation)),
		[generateDraft, createIssueDraft],
	);
}

/**
 * The pre-submit block error, kept with the input that caused it so it's only cleared once the
 * input changes to something else. Without that, the change event a paste itself fires would
 * wipe the warning the paste just raised.
 */
function useBlockError() {
	const [block, setBlock] = useState<{ code: SocialPostErrorCode; input: string } | undefined>(undefined);
	const blockSetterFor = useCallback(
		(input: string) => (code: SocialPostErrorCode | undefined) =>
			setBlock(code ? { code, input: input.trim() } : undefined),
		[],
	);
	const clearBlockUnless = useCallback((value: string) => {
		setBlock((current) => (current && value.trim() === current.input ? current : undefined));
	}, []);
	return { blockError: block?.code, blockSetterFor, clearBlockUnless };
}

export function useSocialPostUrlForm(): SocialPostUrlFormHookResult {
	const track = useTracking();
	const navigate = useNavigate();
	const { classifyOptions, arm } = useArm();

	const generateDraftMutation = useGenerateSocialPostDraft();
	const createIssueDraftMutation = useCreateIssueDraft({ sourceLocation: 'homepage_tiktok' });

	// A supported link: kicks off its preview immediately, but doesn't show a chip until that
	// preview resolves successfully (see `chip` below).
	const [attachedPost, setAttachedPost] = useState<AttachedPost | undefined>(undefined);
	// Cheap title/image/caption lookup for the attached post. Disabled while no post is attached.
	const previewQuery = useSocialPostPreview(attachedPost);
	// A pre-submit block (unsupported platform / invalid link) surfaced the moment a bad link
	// is pasted or submitted.
	const { blockError, blockSetterFor, clearBlockUnless } = useBlockError();

	const previewSuccess = getPreviewSuccess(previewQuery.data);
	const previewErrored =
		attachedPost !== undefined && (previewQuery.isError || previewQuery.data?.__typename === 'SocialPostPreviewError');
	const isResolvingPreview = attachedPost !== undefined && previewQuery.isPending;

	const isSubmitting = useIsSubmitting(generateDraftMutation, createIssueDraftMutation);

	const error = useMemo<SocialPostErrorCode | undefined>(() => {
		if (generateDraftMutation.isError) return mapReasonToErrorCode(generateDraftMutation.error.message);
		if (createIssueDraftMutation.isError) return 'generic_error';
		if (previewErrored) return mapPreviewErrorToCode(previewQuery.data);
		return blockError;
	}, [generateDraftMutation, createIssueDraftMutation, previewErrored, previewQuery.data, blockError]);

	const onRemoveChip = useCallback(() => {
		if (attachedPost) {
			// Distinguishes changed-their-mind (dismissed while still neutral, unsubmitted) from
			// gave-up-after-failure (dismissed once the chip had flipped to its error state).
			void track('homepage_social_post_chip_dismissed', { source: attachedPost.url, error: error !== undefined });
		}
		setAttachedPost(undefined);
		generateDraftMutation.reset();
	}, [track, attachedPost, error, generateDraftMutation]);

	const onPaste = useCallback(
		(event: ClipboardEvent<HTMLTextAreaElement>) => {
			const text = event.clipboardData.getData('text');
			handlePastedInput(text, {
				classifyOptions,
				track,
				setAttachedPost,
				setBlockError: blockSetterFor(text),
				resetIssueDraftError: () => {
					if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
				},
			});
		},
		[createIssueDraftMutation, classifyOptions, track, blockSetterFor],
	);

	const onInputChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>) => {
			clearBlockUnless(event.target.value);
			// A failed preview only ever shows the textarea again once its chip never rendered;
			// clear it so resuming typing starts from a clean slate rather than a stale attachment.
			if (attachedPost && previewErrored) setAttachedPost(undefined);
			// A failed freeform submit's error would otherwise stay visible while the user edits.
			if (createIssueDraftMutation.isError) createIssueDraftMutation.reset();
		},
		[clearBlockUnless, attachedPost, previewErrored, createIssueDraftMutation],
	);

	const onSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (attachedPost) {
				void track('homepage_social_post_click', { cta: 'primary', prompt: attachedPost.url });
				// Not yet resolved (still loading, or resolved to an error): nothing to submit yet.
				if (isSubmitting || !previewSuccess) return;
				generateDraftMutation.mutate({ url: attachedPost.url, caption: previewSuccess.caption ?? undefined });
				return;
			}

			const formData = new FormData(event.target as HTMLFormElement);
			const trimmed = ((formData.get('prompt_or_url') as string) || '').trim();
			submitFreshInput(trimmed, {
				classifyOptions,
				track,
				navigate,
				isSubmitting,
				setAttachedPost,
				setBlockError: blockSetterFor(trimmed),
				createIssueDraftMutate: createIssueDraftMutation.mutate,
			});
		},
		[
			track,
			isSubmitting,
			attachedPost,
			previewSuccess,
			generateDraftMutation,
			createIssueDraftMutation,
			navigate,
			classifyOptions,
			blockSetterFor,
		],
	);

	return {
		data: {
			isSubmitting,
			isResolvingPreview,
			error,
			chip: toChip(attachedPost, previewSuccess),
			...arm,
		},
		actions: {
			onSubmit,
			onPaste,
			onInputChange,
			onRemoveChip,
		},
	};
}
